// react
import { ReactNode } from 'react';

// mui
import {
	Box,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	Switch,
	TextField,
	Typography,
} from '@mui/material';

// icons
import { CalendarMonth as IconCalendar } from '@mui/icons-material';

// types
import { CustomColumnDefinition } from '../../../@types';

// Props

export interface CustomFieldsFormSectionProps {
	/** Column definitions (from project.customXxxColumns) */
	columns: CustomColumnDefinition[];
	/** Current custom data values */
	customData: Record<string, unknown>;
	/** Callback when a field value changes */
	onChange: (columnId: string, value: unknown) => void;
	/** Optional section title (defaults to "Custom Fields") */
	title?: string;
	/** Whether to show the section divider/header */
	showHeader?: boolean;
}

// Component

/**
 * Reusable form section that renders custom field inputs based on
 * CustomColumnDefinition[]. Used by Activity, Work Plan, Contract,
 * and Beneficiary create/edit forms.
 */
export const CustomFieldsFormSection = ({
	columns,
	customData,
	onChange,
	title = 'Custom Fields',
	showHeader = true,
}: CustomFieldsFormSectionProps) => {
	// Only render active columns, sorted by order
	const activeColumns = columns.filter((col) => col.isActive).sort((a, b) => a.order - b.order);

	if (activeColumns.length === 0) return null;

	const renderField = (column: CustomColumnDefinition): ReactNode => {
		const value = customData[column.id];

		switch (column.type) {
			case 'boolean':
				return (
					<Grid item key={column.id} xs={12} md={6}>
						<FormControlLabel
							control={
								<Switch
									checked={Boolean(value)}
									onChange={(e) => onChange(column.id, e.target.checked)}
								/>
							}
							label={
								<Box>
									<Typography variant="body2">
										{column.name}
										{column.isRequired && (
											<Typography component="span" color="error" sx={{ ml: 0.5 }}>
												*
											</Typography>
										)}
									</Typography>
								</Box>
							}
							sx={{
								border: '1px solid',
								borderColor: 'divider',
								borderRadius: 1,
								m: 0,
								p: 1.5,
								width: '100%',
							}}
						/>
					</Grid>
				);

			case 'date':
				return (
					<Grid item key={column.id} xs={12} md={6}>
						<TextField
							fullWidth
							InputLabelProps={{ shrink: true }}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<IconCalendar color="action" />
									</InputAdornment>
								),
							}}
							label={column.name}
							onChange={(e) => onChange(column.id, e.target.value)}
							required={column.isRequired}
							type="date"
							value={value || ''}
						/>
					</Grid>
				);

			case 'number':
				return (
					<Grid item key={column.id} xs={12} md={6}>
						<TextField
							fullWidth
							label={column.name}
							onChange={(e) => onChange(column.id, e.target.value ? Number(e.target.value) : '')}
							required={column.isRequired}
							type="number"
							value={value ?? ''}
						/>
					</Grid>
				);

			case 'select':
				return (
					<Grid item key={column.id} xs={12} md={6}>
						<FormControl fullWidth required={column.isRequired}>
							<InputLabel>{column.name}</InputLabel>
							<Select
								label={column.name}
								onChange={(e) => onChange(column.id, e.target.value)}
								value={(value as string) || ''}>
								<MenuItem value="">
									<em>None</em>
								</MenuItem>
								{column.options?.map((option) => (
									<MenuItem key={option} value={option}>
										{option}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
				);

			case 'text':
			default:
				return (
					<Grid item key={column.id} xs={12} md={6}>
						<TextField
							fullWidth
							label={column.name}
							onChange={(e) => onChange(column.id, e.target.value)}
							required={column.isRequired}
							value={(value as string) || ''}
						/>
					</Grid>
				);
		}
	};

	return (
		<>
			{showHeader && (
				<Grid item xs={12}>
					<Divider sx={{ my: 1 }} />
					<Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
						{title}
					</Typography>
				</Grid>
			)}
			{activeColumns.map(renderField)}
		</>
	);
};

// Validation helper

/**
 * Validate that all required custom fields have values.
 * Returns an array of missing field names, or empty array if valid.
 */
export const validateCustomFields = (
	columns: CustomColumnDefinition[],
	customData: Record<string, unknown>
): string[] => {
	const missing: string[] = [];

	columns
		.filter((col) => col.isActive && col.isRequired)
		.forEach((col) => {
			const value = customData[col.id];
			if (value === undefined || value === null || value === '') {
				missing.push(col.name);
			}
		});

	return missing;
};

// Stats helper

/**
 * Convert custom column definitions + data into stats array for HeaderDetailPage.
 * Only includes active columns that have a value set.
 */
export const customFieldsToStats = (
	columns: CustomColumnDefinition[],
	customData: Record<string, unknown>
): { label: string; value: string }[] => {
	return columns
		.filter((col) => col.isActive)
		.sort((a, b) => a.order - b.order)
		.map((col) => {
			const rawValue = customData?.[col.id];

			// Format value based on type
			let displayValue: string;
			if (rawValue === undefined || rawValue === null || rawValue === '') {
				displayValue = '—';
			} else if (col.type === 'boolean') {
				displayValue = rawValue ? 'Yes' : 'No';
			} else if (col.type === 'date' && typeof rawValue === 'string') {
				try {
					displayValue = new Date(rawValue).toLocaleDateString('en-US', {
						timeZone: 'UTC',
						month: 'short',
						day: 'numeric',
						year: 'numeric',
					});
				} catch {
					displayValue = String(rawValue);
				}
			} else if (col.type === 'number') {
				displayValue = String(rawValue);
			} else {
				displayValue = String(rawValue);
			}

			return {
				label: col.name,
				value: displayValue,
			};
		})
		.filter((stat) => stat.value !== '—');
};
